/** Read-only labelled values on a record panel's field grid — values the record shows but
 *  never edits here (a booking's pre-check-in answers, the operator settings a tour
 *  inherits). A `<dl>`: each fact is a muted caption label over its value, on the vendor
 *  value's geometry (a 24 px label row, the value, a 12 px foot; no input height, no caption
 *  line), so it reads the same in view and edit mode and sits on the same pinned columns as
 *  the FieldSlots around it (Zero-Re-Layout). An empty value is the em dash. */
import { Box, Typography } from '@mui/material';
import type { ReactNode } from 'react';

import { FieldHelp } from '@/components/FieldHelp';

import { EMPTY_DISPLAY } from '../editor/format';

import { FIELD_GRID_SX } from './fieldGrid';

export interface Fact {
  key: string;
  label: string;
  /** null, undefined or '' read as the em dash. */
  value: ReactNode;
  /** Takes the whole row (long text, a list) — decided by the value's kind, never the mode. */
  wide?: boolean;
  /** The label's help popover (the catalog's help, where the fact has one). */
  help?: string;
}

export function FactGrid({ facts }: { facts: readonly Fact[] }) {
  if (facts.length === 0) return null;
  return (
    <Box component="dl" sx={{ ...FIELD_GRID_SX, m: 0 }}>
      {facts.map((f) => {
        const empty = f.value === null || f.value === undefined || f.value === '';
        return (
          <Box
            key={f.key}
            data-fact={f.key}
            {...(f.wide ? { 'data-wide': true } : {})}
            sx={{ minWidth: 0, pb: 1.5, ...(f.wide ? { gridColumn: '1 / -1' } : {}) }}
          >
            <Box component="dt" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minHeight: 24, minWidth: 0 }}>
              <Typography variant="caption" color="text.secondary" sx={{ minWidth: 0 }}>
                {f.label}
              </Typography>
              {f.help && <FieldHelp help={{ title: f.label, body: f.help }} />}
            </Box>
            <Typography
              component="dd"
              variant="body2"
              sx={{
                m: 0,
                minWidth: 0,
                overflowWrap: 'anywhere',
                // A text keeps its line breaks; a rendered list lays itself out.
                ...(typeof f.value === 'string' ? { whiteSpace: 'pre-wrap' } : {}),
                color: empty ? 'text.disabled' : 'text.primary',
              }}
            >
              {empty ? EMPTY_DISPLAY : f.value}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
}
